import { useState, useEffect, useCallback } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { useAgentStore } from '../stores';
import { CreateAgentModal } from './AgentManager';
import { UserBadge } from './UserBadge';
import { DISABLED_LABEL } from '../lib/agent-state';

interface Props {
  selectedAgentId: string | null;
  onSelectAgent: (agentId: string) => void;
}

export default function AgentSidebar({ selectedAgentId, onSelectAgent }: Props) {
  const agents = useAgentStore(s => s.agents);
  const loadFailed = useAgentStore(s => s.loadFailed);
  const loadAgents = useAgentStore(s => s.loadAgents);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      await loadAgents();
    } finally {
      setLoading(false);
    }
  }, [loadAgents]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <span className="sidebar-title">My Agents</span>
        <div className="sidebar-actions">
          <button
            className="icon-btn"
            title="Create Agent"
            onClick={() => setShowCreate(true)}
          >
            <PlusOutlined />
          </button>
        </div>
      </div>

      <div className="channel-list">
        {loading ? (
          <div className="sidebar-empty">Loading...</div>
        ) : agents.length === 0 && loadFailed ? (
          // An owner with agents must never be told they have none.
          <div className="sidebar-empty" data-testid="agent-sidebar-load-failed">
            Couldn’t load your agents.
            <div className="sidebar-empty-actions">
              <button className="btn btn-sm" onClick={() => { setLoading(true); void load(); }}>Try again</button>
            </div>
          </div>
        ) : agents.length === 0 ? (
          <div className="sidebar-empty">No agents yet</div>
        ) : (
          agents.map(agent => (
            <button
              key={agent.id}
              className={`channel-item${selectedAgentId === agent.id ? ' channel-item-active' : ''}${agent.disabled ? ' channel-item-disabled' : ''}`}
              onClick={() => onSelectAgent(agent.id)}
            >
              <UserBadge
                userId={agent.id}
                displayName={agent.display_name}
                role="agent"
                avatarUrl={agent.avatar_url}
                state={agent.state}
                disabled={agent.disabled}
                size={28}
                style={{ flex: 1, minWidth: 0 }}
              />
              {agent.disabled && <span className="agent-disabled-badge">{DISABLED_LABEL}</span>}
            </button>
          ))
        )}
      </div>

      {showCreate && (
        <CreateAgentModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}
